import { call, cancel, cancelled, fork, select, take } from 'redux-saga/effects';
import type { Task } from '@redux-saga/types';
import type { Dispatch } from 'redux';
import { Duration, Effect, Ref, Schedule } from 'effect';
import { createScopedWebSocketEffect, reportWSConnectionError } from '@/domain/ws/clientScoped';
import { globalChannelMessageHandler } from '@/domain/ws/global';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { signInAction, signOutAction, isAuthorizedSelector } from '@/domain/env';

// The client reconnects a dropped connection on its own but stays stopped after a failed first start.
const firstStartRetry = Schedule.exponential('1 second').pipe(
  Schedule.modifyDelay((_, delay) => Duration.min(delay, Duration.minutes(5))),
);

// One report per outage: every attempt but the first fails for the same reason.
const connect = (dispatch: Dispatch) =>
  Effect.gen(function* () {
    const isReported = yield* Ref.make(false);
    const context = {
      hub: getRuntimeConfig().env.REACT_APP_PUBSUB_NOTIFICATIONS_HUB,
      user_context: { page: 'global' },
    };
    return yield* createScopedWebSocketEffect({ context, onMessage: globalChannelMessageHandler(dispatch) }).pipe(
      Effect.tapError((error) =>
        Ref.getAndSet(isReported, true).pipe(
          Effect.flatMap((wasReported) => (wasReported ? Effect.void : reportWSConnectionError(error, context))),
        ),
      ),
      Effect.retry(firstStartRetry),
    );
  });

function* runChannel(dispatch: Dispatch) {
  const controller = new AbortController();
  try {
    yield call(() => Effect.runPromiseExit(connect(dispatch), { signal: controller.signal }));
  } finally {
    if ((yield cancelled()) as boolean) controller.abort();
  }
}

export default function* globalChannel(dispatch: Dispatch) {
  while (true) {
    const isAuthorized = (yield select(isAuthorizedSelector)) as boolean;
    if (!isAuthorized) yield take(signInAction.success);
    const task = (yield fork(runChannel, dispatch)) as Task;
    yield take(signOutAction.success);
    yield cancel(task);
  }
}
